import React, { useState, useEffect } from 'react';
import axios from '../services/axiosInstance';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  UsersIcon,
  RefreshCwIcon,
  PlusIcon,
  UserIcon,
  CrownIcon,
  EditIcon,
  TrashIcon,
  CheckIcon,
  XIcon,
  ArrowRightIcon
} from 'lucide-react';
import { toast } from 'sonner';

const SimpleRoleHierarchy = () => {
  const [roles, setRoles] = useState([]);
  const [hierarchies, setHierarchies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // PERSON-BASED hierarchy state
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [selectedManager, setSelectedManager] = useState('');
  const [peopleInPosition, setPeopleInPosition] = useState([]);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editManagerId, setEditManagerId] = useState('');

  // POSITION-BASED hierarchy state
  const [positionHierarchy, setPositionHierarchy] = useState([]);
  const [selectedPosForHierarchy, setSelectedPosForHierarchy] = useState('');
  const [selectedManagerPos, setSelectedManagerPos] = useState('');

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesResponse, hierarchiesResponse, posHierarchyResponse] = await Promise.all([
        axios.get(`/roles`),
        axios.get(`/hierarchy`),
        axios.get(`/position-hierarchy`).catch(() => ({ data: [] }))
      ]);
      
      setRoles(rolesResponse.data);
      // Ensure hierarchies is always an array
      const hierarchiesData = Array.isArray(hierarchiesResponse.data) 
        ? hierarchiesResponse.data 
        : [];
      setHierarchies(hierarchiesData);
      
      // Set position hierarchy data
      const posHierarchyData = Array.isArray(posHierarchyResponse.data)
        ? posHierarchyResponse.data
        : [];
      setPositionHierarchy(posHierarchyData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle position selection
  const handlePositionChange = async (positionRoleId) => {
    setSelectedPosition(positionRoleId);
    setSelectedPerson('');
    setSelectedManager('');
    
    if (positionRoleId) {
      try {
        // Fetch people in the selected position
        const response = await axios.get(`/roles/${positionRoleId}/users`);
        setPeopleInPosition(response.data);
        
        // If only one person in position, auto-select them
        if (response.data.length === 1) {
          setSelectedPerson(response.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching people in position:', error);
        setPeopleInPosition([]);
      }
    } else {
      setPeopleInPosition([]);
    }
  };

  // No filtering rules - all roles can be manager for anyone
  // This is removed as per new requirements

  // Add new hierarchy
  const addHierarchy = async () => {
    if (!selectedPosition || !selectedPerson || !selectedManager) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    // Check if this user already has a hierarchy
    const existingHierarchy = hierarchies.find(h => h.user_id === selectedPerson);
    if (existingHierarchy) {
      toast.error('Bu kullanıcı için zaten hiyerarşi tanımlanmış');
      return;
    }

    try {
      setSaving(true);
      
      // Use PUT for role-based hierarchy (create or update)
      await axios.put(`/hierarchy/role/${selectedPerson}`, {
        manager_role_id: selectedManager
      });

      await fetchData();
      setSelectedPosition('');
      setSelectedPerson('');
      setSelectedManager('');
      setPeopleInPosition([]);
      toast.success('Hiyerarşi eklendi');
    } catch (error) {
      console.error('Error adding hierarchy:', error);
      toast.error('Hiyerarşi eklenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const startEdit = (hierarchy) => {
    setEditingId(hierarchy.id);
    setEditManagerId(hierarchy.manager_role_id || '');
  };

  // Save edit
  const saveEdit = async (hierarchyId) => {
    try {
      setSaving(true);
      await axios.put(`/hierarchy/${hierarchyId}`, {
        manager_role_id: editManagerId
      });

      await fetchData();
      setEditingId(null);
      setEditManagerId('');
      toast.success('Hiyerarşi güncellendi');
    } catch (error) {
      console.error('Error updating hierarchy:', error);
      toast.error('Hiyerarşi güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingId(null);
    setEditManagerId('');
  };

  // Delete hierarchy
  const deleteHierarchy = async (hierarchyId, userName) => {
    if (!window.confirm(`${userName} için hiyerarşi ilişkisini silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      setSaving(true);
      await axios.delete(`/hierarchy/${hierarchyId}`);
      
      await fetchData();
      toast.success('Hiyerarşi silindi');
    } catch (error) {
      console.error('Error deleting hierarchy:', error);
      toast.error('Hiyerarşi silinirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // ===== POSITION-BASED HIERARCHY FUNCTIONS =====
  
  const addPositionHierarchy = async () => {
    if (!selectedPosForHierarchy || !selectedManagerPos) {
      return;
    }

    try {
      setSaving(true);
      
      const requestData = {
        position_id: selectedPosForHierarchy,
        manager_position_id: selectedManagerPos
      };
      
      console.log('=== Position Hierarchy Request ===');
      console.log('Position ID:', selectedPosForHierarchy);
      console.log('Position ID length:', selectedPosForHierarchy.length);
      console.log('Manager Position ID:', selectedManagerPos);
      console.log('Manager Position ID length:', selectedManagerPos.length);
      console.log('Request body:', requestData);
      
      const response = await axios.post(`/position-hierarchy`, requestData);
      
      console.log('✅ Success:', response.data);

      // Refresh data without showing loading state
      const [rolesResponse, hierarchiesResponse, posHierarchyResponse] = await Promise.all([
        axios.get(`/roles`),
        axios.get(`/hierarchy`),
        axios.get(`/position-hierarchy`).catch(() => ({ data: [] }))
      ]);
      
      setRoles(rolesResponse.data);
      setHierarchies(Array.isArray(hierarchiesResponse.data) ? hierarchiesResponse.data : []);
      setPositionHierarchy(Array.isArray(posHierarchyResponse.data) ? posHierarchyResponse.data : []);
      
      setSelectedPosForHierarchy('');
      setSelectedManagerPos('');
      toast.success('Pozisyon hiyerarşisi eklendi');
    } catch (error) {
      console.error('❌ Error adding position hierarchy:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Full error:', {
        message: error.message,
        config: error.config,
        response: error.response
      });
      
      const errorMsg = error.response?.data?.detail || error.message || 'Hata oluştu';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const deletePositionHierarchy = async (positionId, positionName) => {
    if (!window.confirm(`${positionName} için varsayılan yönetici tanımını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      setSaving(true);
      await axios.delete(`/position-hierarchy/${positionId}`);
      
      await fetchData();
      toast.success('Pozisyon hiyerarşisi silindi');
    } catch (error) {
      console.error('Error deleting position hierarchy:', error);
      toast.error('Hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCwIcon className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Hiyerarşi yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <UsersIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pozisyon Hiyerarşisi</h1>
            <p className="text-gray-600">
              Pozisyonlar arası hiyerarşi ilişkilerini tanımlayın
            </p>
          </div>
        </div>

        <div className="flex space-x-2">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCwIcon className="w-4 h-4" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Add New Hierarchy */}
      <Card>
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Yeni Hiyerarşi Ekle
          </h2>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {/* 1. Position Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserIcon className="w-4 h-4 inline mr-1" />
                Pozisyon
              </label>
              <Select value={selectedPosition} onValueChange={handlePositionChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Pozisyon seçin" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 2. Person Dropdown - Always show when position is selected */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserIcon className="w-4 h-4 inline mr-1" />
                Kişi
              </label>
              <Select 
                value={selectedPerson} 
                onValueChange={setSelectedPerson}
                disabled={!selectedPosition}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kişi seçin" />
                </SelectTrigger>
                <SelectContent>
                  {peopleInPosition.map((person) => (
                    <SelectItem key={person.id} value={person.id}>
                      {person.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 3. Manager Position Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CrownIcon className="w-4 h-4 inline mr-1" />
                Yönetici Pozisyonu
              </label>
              <Select 
                value={selectedManager} 
                onValueChange={setSelectedManager}
                disabled={!selectedPerson}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Yönetici pozisyonu seçin" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Add Button */}
            <div>
              <Button 
                onClick={addHierarchy}
                disabled={!selectedPosition || !selectedPerson || !selectedManager || saving}
                className="w-full gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Ekle
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Existing Hierarchies */}
      <Card>
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UsersIcon className="w-5 h-5" />
            Mevcut Hiyerarşiler ({hierarchies.length})
          </h2>
        </div>
        
        <div className="p-6">
          {hierarchies.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Henüz hiyerarşi tanımlanmamış
              </h3>
              <p className="text-gray-600">
                Yukarıdaki formdan hiyerarşi ilişkilerini ekleyin
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {hierarchies.map((hierarchy) => (
                <div key={hierarchy.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-4">
                    {/* User */}
                    <div className="flex items-center space-x-2">
                      <UserIcon className="w-5 h-5 text-blue-600" />
                      <div>
                        <span className="font-medium text-gray-900 block">
                          {hierarchy.user_name}
                        </span>
                        <span className="text-sm text-gray-500">
                          {hierarchy.user_role_name}
                        </span>
                      </div>
                    </div>

                    <ArrowRightIcon className="w-4 h-4 text-gray-400" />

                    {/* Manager */}
                    {editingId === hierarchy.id ? (
                      <Select value={editManagerId} onValueChange={setEditManagerId}>
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.id} value={role.id}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <CrownIcon className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-gray-900">
                          {hierarchy.manager_role_name || 'Yönetici Yok'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {editingId === hierarchy.id ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => saveEdit(hierarchy.id)}
                          disabled={saving}
                          className="gap-1"
                        >
                          <CheckIcon className="w-4 h-4" />
                          Kaydet
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEdit}
                          disabled={saving}
                          className="gap-1"
                        >
                          <XIcon className="w-4 h-4" />
                          İptal
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(hierarchy)}
                          className="gap-1"
                        >
                          <EditIcon className="w-4 h-4" />
                          Düzenle
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteHierarchy(hierarchy.id, hierarchy.user_name)}
                          className="gap-1 text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Sil
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Instructions */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
          <UsersIcon className="w-4 h-4 mr-2" />
          Nasıl Çalışır?
        </h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>1. Pozisyon:</strong> Önce hangi pozisyonu yönetilecek kişi olduğunu seçin</li>
          <li>• <strong>2. Kişi:</strong> O pozisyondaki kişilerden birini seçin</li>
          <li>• <strong>3. Yönetici Pozisyonu:</strong> Bu kişiye yönetici olacak pozisyonu seçin</li>
          <li>• <strong>Herkes Herkesi Yönetebilir:</strong> Hiçbir kısıtlama yoktur</li>
          <li>• <strong>Basit Sistem:</strong> 3 adımda hiyerarşi kurulur</li>
        </ul>
      </Card>

      {/* ===== POSITION-BASED HIERARCHY SECTION ===== */}
      <div className="mt-12 pt-8 border-t-2 border-gray-300">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Pozisyon Bazlı Varsayılan Hiyerarşi</h2>
            <p className="text-sm text-gray-600 mt-1">Pozisyonlar arası yönetim ilişkileri (kişiye bağımsız)</p>
          </div>
          <div className="text-sm text-gray-500">
            {positionHierarchy.length} tanım
          </div>
        </div>

        {/* Add New Position Hierarchy */}
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Yeni Pozisyon Hiyerarşisi Ekle</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Position Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pozisyon
              </label>
              <Select value={selectedPosForHierarchy} onValueChange={setSelectedPosForHierarchy}>
                <SelectTrigger>
                  <SelectValue placeholder="Pozisyon seçin" />
                </SelectTrigger>
                <SelectContent>
                  {roles.filter(r => !positionHierarchy.find(ph => ph.position_id === r.id)).map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Manager Position Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yönetici Pozisyon
              </label>
              <Select value={selectedManagerPos} onValueChange={setSelectedManagerPos} disabled={!selectedPosForHierarchy}>
                <SelectTrigger>
                  <SelectValue placeholder="Yönetici pozisyon seçin" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                      {role.id === selectedPosForHierarchy && ' (Kendisi)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Add Button */}
            <div className="flex items-end">
              <Button 
                onClick={addPositionHierarchy}
                disabled={!selectedPosForHierarchy || !selectedManagerPos || saving}
                className="w-full"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Ekle
              </Button>
            </div>
          </div>
        </Card>

        {/* Position Hierarchy List */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Mevcut Pozisyon Hiyerarşileri</h3>
            {positionHierarchy.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Henüz pozisyon hiyerarşisi tanımlanmamış</p>
            ) : (
              <div className="space-y-3">
                {positionHierarchy.map((item) => (
                  <div key={item.position_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                    <div className="flex items-center space-x-4">
                      <div className="text-sm">
                        <span className="font-medium text-gray-900">{item.position_name}</span>
                        <ArrowRightIcon className="w-4 h-4 inline mx-2 text-gray-400" />
                        <span className="font-medium text-blue-600">{item.manager_position_name}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deletePositionHierarchy(item.position_id, item.position_name)}
                      disabled={saving}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Info */}
        <Card className="p-4 bg-green-50 border-green-200 mt-4">
          <h3 className="font-semibold text-green-800 mb-2">
            📌 Nasıl Çalışır?
          </h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• Pozisyon bazlı: Hangi pozisyon hangi pozisyona rapor verir</li>
            <li>• Kişiye bağımsız: Yeni kullanıcı eklendiğinde otomatik yöneticisi belirlenir</li>
            <li>• Öncelik: Kişi bazlı tanımlar bu tanımların üzerine yazar</li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default SimpleRoleHierarchy;